import { useCallback, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { SCENARIOS } from './data/scenarios';
import type { Category, LifetimeStats, ScenarioCard, SessionRecord } from './data/types';
import { ReturnLink } from './components/ReturnLink';
import { Welcome } from './components/Welcome';
import { ProfileCard } from './components/ProfileCard';
import { Feedback } from './components/Feedback';
import { Summary } from './components/Summary';
import styles from './App.module.css';

type Phase = 'welcome' | 'playing' | 'summary';

const BEST_KEY = 'rfgf:best-accuracy';
const LAST_KEY = 'rfgf:last-session';
const LIFETIME_KEY = 'rfgf:lifetime';
const ROUND_SIZE = 12;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

function emptyByCategory(): Record<Category, { right: number; wrong: number }> {
  return {
    isolation: { right: 0, wrong: 0 },
    'love-bombing': { right: 0, wrong: 0 },
    dismissiveness: { right: 0, wrong: 0 },
    'fast-escalation': { right: 0, wrong: 0 },
    'limit-pushing': { right: 0, wrong: 0 },
    deflection: { right: 0, wrong: 0 },
    respect: { right: 0, wrong: 0 },
    support: { right: 0, wrong: 0 },
    transparency: { right: 0, wrong: 0 },
    'vetting-readiness': { right: 0, wrong: 0 },
    'self-awareness': { right: 0, wrong: 0 },
  };
}

function emptyLifetime(): LifetimeStats {
  return {
    roundsPlayed: 0,
    totalAnswered: 0,
    totalCorrect: 0,
    byCategory: emptyByCategory(),
  };
}

function mergeLifetime(prev: LifetimeStats, session: SessionRecord): LifetimeStats {
  const byCategory = { ...prev.byCategory };
  for (const [cat, counts] of Object.entries(session.byCategory) as [
    Category,
    { right: number; wrong: number },
  ][]) {
    byCategory[cat] = {
      right: prev.byCategory[cat].right + counts.right,
      wrong: prev.byCategory[cat].wrong + counts.wrong,
    };
  }
  return {
    roundsPlayed: prev.roundsPlayed + 1,
    totalAnswered: prev.totalAnswered + session.total,
    totalCorrect: prev.totalCorrect + session.correct,
    byCategory,
  };
}

export function App() {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [deck, setDeck] = useState<ScenarioCard[]>([]);
  const [index, setIndex] = useState(0);
  const [userPick, setUserPick] = useState<'green' | 'red' | null>(null);
  const [byCategory, setByCategory] = useState(emptyByCategory);
  const [bestAccuracy, setBestAccuracy] = useState<number | null>(() =>
    storage.get<number | null>(BEST_KEY, null)
  );
  const [lastSession, setLastSession] = useState<SessionRecord | null>(() =>
    storage.get<SessionRecord | null>(LAST_KEY, null)
  );
  const [lifetime, setLifetime] = useState<LifetimeStats>(() =>
    storage.get<LifetimeStats>(LIFETIME_KEY, emptyLifetime())
  );

  function begin() {
    setDeck(shuffle(SCENARIOS).slice(0, Math.min(ROUND_SIZE, SCENARIOS.length)));
    setIndex(0);
    setUserPick(null);
    setByCategory(emptyByCategory());
    setPhase('playing');
  }

  const currentCard = deck[index];

  const onCommit = useCallback(
    (direction: 'left' | 'right') => {
      if (!currentCard || userPick !== null) return;
      const pick: 'green' | 'red' = direction === 'right' ? 'green' : 'red';
      setUserPick(pick);
      const correct =
        currentCard.verdict === 'ambiguous'
          ? true
          : currentCard.verdict === pick;
      setByCategory((bc) => {
        const next = { ...bc };
        const cat = currentCard.category;
        next[cat] = {
          right: bc[cat].right + (correct ? 1 : 0),
          wrong: bc[cat].wrong + (correct ? 0 : 1),
        };
        return next;
      });
    },
    [currentCard, userPick]
  );

  function onNext() {
    if (index + 1 < deck.length) {
      setIndex(index + 1);
      setUserPick(null);
    } else {
      // round done — record session
      const totalRight = Object.values(byCategory).reduce((s, c) => s + c.right, 0);
      const session: SessionRecord = {
        date: new Date().toISOString(),
        total: deck.length,
        correct: totalRight,
        byCategory,
      };
      storage.set(LAST_KEY, session);
      setLastSession(session);
      const accuracy = totalRight / deck.length;
      if (bestAccuracy === null || accuracy > bestAccuracy) {
        storage.set(BEST_KEY, accuracy);
        setBestAccuracy(accuracy);
      }
      // Roll this round into lifetime stats so cross-session pattern
      // mastery shows up on the welcome and summary screens.
      const nextLifetime = mergeLifetime(lifetime, session);
      storage.set(LIFETIME_KEY, nextLifetime);
      setLifetime(nextLifetime);
      setPhase('summary');
    }
  }

  // When userPick is set, hold for 5s so user can read feedback, then advance
  // is handled by the Feedback component (auto-advance timer)

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {phase === 'playing' && (
          <span className={styles.progress}>
            {index + 1} of {deck.length}
          </span>
        )}
      </header>

      <main className={styles.stage}>
        {phase === 'welcome' && (
          <Welcome
            bestAccuracy={bestAccuracy}
            lastSession={lastSession}
            lifetime={lifetime}
            onBegin={begin}
          />
        )}

        {phase === 'playing' && currentCard && (
          <div className={styles.gameArea}>
            {userPick === null ? (
              <ProfileCard key={currentCard.id} card={currentCard} onCommit={onCommit} />
            ) : (
              <Feedback
                card={currentCard}
                userVerdict={userPick}
                onNext={onNext}
              />
            )}
          </div>
        )}

        {phase === 'summary' && lastSession && (
          <Summary session={lastSession} lifetime={lifetime} onAnother={begin} />
        )}
      </main>

      <footer className={styles.footer}>
        <p>
          a tool for pattern practice, not for diagnosing real people. trust
          your gut and ask more questions.
        </p>
      </footer>
    </>
  );
}
